import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {t} from 'i18next';
import {
  Menu,
  Typography,
  Divider,
  Box,
  ListItemText,
  MenuItem,
} from '@mui/material';
import {NotificationResponse} from '../../api/types/notifications.type';
import ArrButton from '../common/arr-button/ArrButton';
import {ReactComponent as SuccessIcon} from '../../../src/assets/images/success-icon.svg';
import {ReactComponent as SpinnerIcon} from '../../../src/assets/images/spinner.svg';
import {ReactComponent as ErrorIcon} from '../../../src/assets/images/fail-icon.svg';
import './NotificationsMenu.scss';
import {RootState} from '../../redux/store';
import {
  extractBRID,
  extractReportId,
  getStepName,
  handleDownload,
  isAnalysis,
  NotificationTypes,
  getUrlFromString,
} from '../background-tasks/backgroundTasksUtils';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../shared/constants';
import {getCustomIndexName} from '../../utils/utils';
import {formatPendingSearchIdsForErrorMessage} from '../session-search/utilities/nestedSearchUtils';

interface NotificationsMenuProps {
  notificationsList: NotificationResponse[];
  anchorEl: null | HTMLElement;
  open: boolean;
  onClose: (restoreFocus?: boolean) => void;
}

const NotificationsMenu: React.FC<NotificationsMenuProps> = ({
  notificationsList,
  anchorEl,
  open,
  onClose,
}) => {
  const [showErrorToast, setShowErrorToast] = useState(false);
  const navigate = useNavigate();
  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );
  const [activeIndex, setActiveIndex] = useState(0);

  const headerRef = useRef<HTMLHeadingElement | null>(null);

  const itemRefs = useRef<Array<HTMLLIElement | null>>([]);

  useEffect(() => {
    if (!open) return;

    setActiveIndex(0);

    requestAnimationFrame(() => {
      headerRef.current?.focus();
    });
  }, [open]);

  const focusItem = (index: number) => {
    if (!notificationsList.length) return;
    const nextIndex =
      (index + notificationsList.length) % notificationsList.length;

    setActiveIndex(nextIndex);
    itemRefs.current[nextIndex]?.focus();
  };

  const focusViewAllButton = () => {
    const button = document.querySelector(
      '[data-testid="viewall-button"]'
    ) as HTMLElement | null;

    if (button) {
      button.focus();
      return true;
    }
    return false;
  };

  const handleErrorToast = (val: boolean = false) => {
    setShowErrorToast(val);
  };

  const handleNotificationClick = (notification: NotificationResponse) => {
    handleDownload(notification, handleErrorToast);
  };

  const handleNavigation = (notification: NotificationResponse) => {
    switch (notification.notificationTypeCode) {
      case NotificationTypes.CATEGORIZATION:
        navigate(`/Proview/Proview?proviewId=${notification.id}`);
        onClose();
        break;
      case NotificationTypes.BACKGROUNDREPORT:
        navigate(
          getUrlFromString(notification.remarks, notification.id, false),
          {
            state: {
              isBackgroundReport: true,
              reportId: extractReportId(notification?.remarks),
            },
          }
        );
        onClose();
        //Hard refresh to reload the page on same route navigation since the report page needs to be re-rendered while client side selection
        setTimeout(() => {
          navigate(0);
        }, 0);
        break;
      default:
        break;
    }
  };

  const getNotificationMessage = (notification: NotificationResponse) => {
    const {
      notificationTypeCode,
      remarks,
      notificationStatusCode,
      id,
      notificationID,
      securityGroupLabel,
    } = notification;
    switch (notificationTypeCode) {
      case NotificationTypes.CONCEPTCLUSTERSUMMARYREPORT: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: `${t('notifications.messages.conceptClusterSummaryReport')}`,
            isDownloadable: true,
          };
        } else {
          return {
            message: `${t('notifications.messages.exportReportError')}`, //need to update message based on failure case
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.INGESTIONSUMMARY: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: `${t('notifications.messages.ingestionSummaryReport')} ${t('notifications.messages.generalMsg')}`,
            isDownloadable: true,
          };
        } else {
          return {
            message: `${t('notifications.messages.ingestionSummaryReport')} ${t('notifications.messages.generalMsg')}`,
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.EXCELPROCERROREXPORT: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.excelProcessingErrorReport'),
            isDownloadable: true,
          };
        } else {
          return {
            message: t('notifications.messages.exportReportError', {
              notificationId: notificationID,
            }),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.EXPORTREPORT: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: `${t('notifications.messages.exportReportCompleted')} ${t('notifications.messages.generalMsg')}`,
            isDownloadable: true,
          };
        } else {
          return {
            message: t('notifications.messages.exportReportError', {
              notificationId: notificationID,
            }),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.PROJECT: {
        return {
          message: remarks,
          isDownloadable: false,
        };
      }
      case NotificationTypes.PRODUCTIONBATESRANGE: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.productionBatesRange'),
            isDownloadable: true,
          };
        } else {
          return {
            message: t('notifications.messages.generalError'),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.DOCUMENTPRINTER: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.printDocumentSuccess', {
              docId: id,
            }),
            isDownloadable: true,
          };
        } else {
          return {
            message: t('notifications.messages.generalError'),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.BATCHPRINT: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.batchPrintSuccess', {
              id: id,
            }),
            isDownloadable: true,
          };
        } else {
          return {
            message: t('notifications.messages.batchPrintError', {
              id: id,
            }),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.DOMAINDASHBOARDXPORT: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: `${t('notifications.messages.domainDashboardExport')} ${t('notifications.messages.generalMsg').toLowerCase()}`,
            isDownloadable: true,
          };
        } else {
          return {
            message: t('notifications.messages.generalError'),
            isDownloadable: true,
          };
        }
      }
      case NotificationTypes.TRANSCRIPTREPORT: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: `${t('notifications.messages.transcriptExport')}`,
            isDownloadable: true,
          };
        } else {
          return {
            message: t('notifications.messages.generalError'),
            isDownloadable: true,
          };
        }
      }
      case NotificationTypes.BATCHREDACTION: {
        if (notificationStatusCode === 'COMPLETED') {
          if (isAnalysis(remarks)) {
            return {
              message: t(
                'notifications.messages.batchRedactionAnalysisSuccess'
              ),
              isDownloadable: true,
            };
          } else if (remarks === '') {
            return {
              message: t(
                'notifications.messages.batchRedactionRollbackSuccess',
                {id}
              ),
              isDownloadable: false,
            };
          }
          return {
            message: t('notifications.messages.batchRedactionSuccess', {
              Id: extractBRID(remarks),
            }),
            isDownloadable: true,
          };
        } else {
          return {
            message: t('notifications.messages.batchRedactionError', {
              id,
            }),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.CATEGORIZATION: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.categorizationSuccess', {
              notificationId: notificationID,
            }),
            isNavigable: true,
          };
        } else {
          return {
            message: t('notifications.messages.categorizationFailure', {
              notificationId: notificationID,
            }),
            isNavigable: true,
          };
        }
      }
      case NotificationTypes.UNPUBLISHDOCLIST: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.UnPublishDocListSuccess'),
            isDownloadable: true,
          };
        } else {
          return {
            message: t('notifications.messages.UnPublishDocListError'),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.BACKGROUNDREPORT: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.backgroundReportSuccess', {
              notificationId: notificationID,
            }),
            isNavigable: true,
          };
        } else {
          return {
            message: t('notifications.messages.backgroundReportFailure', {
              notificationId: notificationID,
            }),
            isNavigable: true,
          };
        }
      }
      case NotificationTypes.PRODUCTIONCOMMIT: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.productionCommitSuccess', {
              name: remarks,
            }),
            isDownloadable: false,
          };
        }
        return {
          message: remarks || t('notifications.messages.generalError'),
          isDownloadable: false,
        };
        break;
      }
      case NotificationTypes.PRODUCTIONUNCOMMIT: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.productionUncommitSuccess', {
              name: remarks,
            }),
            isDownloadable: false,
          };
        }
        return {
          message: remarks || t('notifications.messages.generalError'),
          isDownloadable: false,
        };
        break;
      }
      case NotificationTypes.PRODUCTIONCANCEL: {
        const moduleLabel = t('notifications.messages.moduleProduction');
        const displayName = remarks || '';

        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.productionCancelSuccess', {
              module: moduleLabel,
              name: displayName,
            }),
            isNavigable: false,
            isDownloadable: false,
          };
        } else if (notificationStatusCode === 'ERROR') {
          return {
            message: t('notifications.messages.productionCancelFailure', {
              module: moduleLabel,
              name: displayName,
            }),
            isNavigable: false,
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.productionCancelFailure', {
              module: moduleLabel,
              name: displayName,
            }),
            isNavigable: false,
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.EXPORTCANCEL: {
        const moduleLabel = t('notifications.messages.moduleExport');
        const displayName = remarks || '';

        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.productionCancelSuccess', {
              module: moduleLabel,
              name: displayName,
            }),
            isNavigable: false,
            isDownloadable: false,
          };
        } else if (notificationStatusCode === 'ERROR') {
          return {
            message: t('notifications.messages.productionCancelFailure', {
              module: moduleLabel,
              name: displayName,
            }),
            isNavigable: false,
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.productionCancelFailure', {
              module: moduleLabel,
              name: displayName,
            }),
            isNavigable: false,
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.SAVEDSEARCH: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.savedSearchSuccess', {
              id,
            }),
            isDownloadable: false,
          };
        }

        if (
          notificationStatusCode === 'ERROR' &&
          remarks != null &&
          String(remarks).trim() !== ''
        ) {
          const trimmedRemarks = String(remarks).trim();
          const startingWord = trimmedRemarks.split(/[\s:]+/)[0]?.toLowerCase();

          if (startingWord === 'inprogress') {
            const searchIdsPart = trimmedRemarks.includes(':')
              ? trimmedRemarks.substring(trimmedRemarks.indexOf(':') + 1).trim()
              : '';
            const searchIds =
              formatPendingSearchIdsForErrorMessage(searchIdsPart);

            return {
              message: t(
                'notifications.messages.savedSearchPendingOrInProgress',
                {
                  id,
                  searchIds,
                }
              ),
              isDownloadable: false,
            };
          }

          if (startingWord === 'circular') {
            return {
              message: t(
                'notifications.messages.savedSearchCircularReference',
                {
                  id,
                }
              ),
              isDownloadable: false,
            };
          }
        }

        return {
          message: t('notifications.messages.savedSearchFailure', {
            id,
          }),
          isDownloadable: false,
        };
      }
      case NotificationTypes.SESSIONSEARCH: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.sessionSearchSuccess', {
              id,
            }),
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.sessionSearchFailure', {
              id,
            }),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.BULKACTIONTAG: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.bulkActionTagSuccess', {id}),
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.bulkActionTagFailure', {id}),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.BULKACTIONUNTAG: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.bulkActionUntagSuccess', {id}),
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.bulkActionUntagFailure', {id}),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.BULKACTIONFOLDER: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.bulkActionFolderSuccess', {id}),
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.bulkActionFolderFailure', {id}),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.BULKACTIONUNFOLDER: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.bulkActionUnfolderSuccess', {
              id,
            }),
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.bulkActionUnfolderFailure', {
              id,
            }),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.BULKACTIONRELEASE: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.bulkActionReleaseSuccess', {id}),
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.bulkActionReleaseFailure', {id}),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.BULKACTIONUNRELEASE: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.bulkActionUnreleaseSuccess', {
              id,
            }),
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.bulkActionUnreleaseFailure', {
              id,
            }),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.BULKACTIONASSIGN: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.bulkActionAssignSuccess', {id}),
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.bulkActionAssignFailure', {id}),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.BULKACTIONUNASSIGN: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.bulkActionUnassignSuccess', {
              id,
            }),
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.bulkActionUnassignFailure', {
              id,
            }),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.QUICKBATCHASSIGN: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.quickBatchSuccess', {id}),
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.quickBatchFailure', {id}),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.AUTOINGESTION: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.autoPublishSuccess', {
              datasetName: remarks.split('||')[0],
            }),
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.autoIngestionFailed', {
              datasetName: remarks.split('||')[0],
              stepName: getStepName(remarks.split('||')[1]),
            }),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.AUTOPUBLISH: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.autoPublishSuccess', {
              datasetName: remarks.split('||')[0],
            }),
            isDownloadable: false,
          };
        }
        return {
          message: remarks || t('notifications.messages.generalError'),
          isDownloadable: false,
        };
        break;
      }
      case NotificationTypes.SGEMAILINCLUSIVE: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.sgEmailInclusiveSuccess', {
              securityGroupName: securityGroupLabel,
            }),
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.sgEmailInclusiveFailure', {
              securityGroupName: securityGroupLabel,
            }),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.REVIEWJOBSEXPORT: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.reviewJobsExportSuccess'),
            isDownloadable: true,
          };
        } else {
          return {
            message: t('notifications.messages.reviewJobsExportFailure'),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.REVIEWRESULTSEXPORT: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('ai.review.metrics.downloadReportNotificationSuccess'),
            isDownloadable: true,
          };
        } else {
          return {
            message: t('ai.review.metrics.downloadReportNotificationFailure'),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.ISSUECODERESULTS: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.issueCodeResultsExportSuccess'),
            isDownloadable: true,
          };
        } else {
          return {
            message: t('notifications.messages.issueCodeResultsExportFailure'),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.RELEVANCYDOCRESULTS: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t(
              'notifications.messages.relevancyDocResultsExportSuccess'
            ),
            isDownloadable: true,
          };
        } else {
          return {
            message: t(
              'notifications.messages.relevancyDocResultsExportFailure'
            ),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.REPRODUCTIONCOMMIT: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.reproductionCommitSuccess', {
              name: remarks,
            }),
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.reproductionCommitFailure', {
              name: remarks,
            }),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.REPRODUCTIONUNCOMMIT: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.reproductionUncommitSuccess', {
              name: remarks,
            }),
            isDownloadable: false,
          };
        } else {
          return {
            message: t('notifications.messages.reproductionUncommitFailure', {
              name: remarks,
            }),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.CUSTOMINDEXCREATE: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.customIndexCreationSuccess', {
              name: remarks,
            }),
            isDownloadable: false,
          };
        } else {
          const indexName = getCustomIndexName(remarks);
          return {
            message: t('notifications.messages.customIndexCreationFailure', {
              name: indexName,
            }),
            isDownloadable: false,
          };
        }
      }
      case NotificationTypes.CUSTOMINDEXREBUILD: {
        if (notificationStatusCode === 'COMPLETED') {
          return {
            message: t('notifications.messages.customIndexRebuildSuccess', {
              name: remarks,
            }),
            isDownloadable: false,
          };
        } else {
          const indexName = getCustomIndexName(remarks);
          return {
            message: t('notifications.messages.customIndexRebuildFailure', {
              name: indexName,
            }),
            isDownloadable: false,
          };
        }
      }
      default:
        return {
          message:
            notification.notificationTypeLabel || t('notifications.general'),
          isDownloadable:
            notificationStatusCode === 'COMPLETED' &&
            /ESIndexingErrorReportID|ExcelProcessingErrorReportID/i.test(
              remarks
            ),
        };
    }
  };

  return (
    <>
      <Menu
        id="notifications-menu"
        className="notifications-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={(_, reason) => {
          if (reason === 'tabKeyDown') return;
          onClose(true);
        }}
        disableAutoFocusItem
        aria-labelledby="notifications-button"
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        autoFocus={false}
        slotProps={{
          paper: {
            elevation: 3,
            className: 'notifications-menu-paper',
            style: {width: '350px'},
          },
          list: {
            variant: 'menu',
            'aria-labelledby': 'notifications-header-id',
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                onClose(true);
              }
            },
          },
        }}
      >
        <Box className="notifications-menu-header" role="presentation">
          <Typography
            ref={headerRef}
            tabIndex={-1}
            className="typo-text body3_size notifications-menu__header-text"
            id="notifications-header-id"
            variant="h6"
            component="h2"
            data-testid={`notifications-menu-background-tasks`}
            onKeyDown={(e) => {
              if (e.key === 'Tab' && !e.shiftKey) {
                e.preventDefault();
                if (roleId > 0 && focusViewAllButton()) {
                  return;
                }
                focusItem(0);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onClose(true);
              }
            }}
          >
            {t('notifications.backgroundTasks')} ({notificationsList.length})
          </Typography>
          {roleId > 0 && (
            <ArrButton
              variant="primary"
              size="medium"
              onClick={() => {
                navigate('/Background/BackgroundTask');
                onClose();
              }}
              aria-label={t('notifications.viewAll')}
              dataTestId="viewall-button"
              onKeyDown={(e) => {
                if (e.key === 'Tab') {
                  e.preventDefault();
                  if (e.shiftKey) {
                    headerRef.current?.focus();
                  } else {
                    focusItem(0);
                  }
                }
              }}
            >
              {t('notifications.viewAll')}
            </ArrButton>
          )}
        </Box>
        <Divider className="notifications-menu__divider" aria-hidden="true" />

        {notificationsList.length > 0 && (
          <Box
            className="notifications-menu__list"
            aria-labelledby="notifications-header-id"
          >
            {notificationsList.map((notification, index) => {
              const {message, isDownloadable, isNavigable} =
                getNotificationMessage(notification);
              const statusText =
                notification.notificationStatusCode === 'COMPLETED'
                  ? t('notifications.completed')
                  : notification.notificationStatusCode === 'ERROR'
                    ? t('notifications.failed')
                    : t('notifications.incompleted');

              return (
                <MenuItem
                  key={notification.notificationID}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  className="notifications-menu__list-item"
                  tabIndex={index === activeIndex ? 0 : -1}
                  onFocus={() => setActiveIndex(index)}
                  onClick={() => {
                    if (isDownloadable) handleNotificationClick(notification);
                    else if (isNavigable) handleNavigation(notification);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Tab') {
                      e.preventDefault();

                      if (e.shiftKey) {
                        if (index === 0) {
                          if (roleId > 0 && focusViewAllButton()) {
                            return;
                          }
                          headerRef.current?.focus();
                        } else {
                          focusItem(index - 1);
                        }
                      } else if (index < notificationsList.length - 1) {
                        focusItem(index + 1);
                      }
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      e.stopPropagation();
                      focusItem(index + 1);
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      e.stopPropagation();
                      focusItem(index - 1);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      onClose(true);
                    }
                  }}
                  data-testid={`notifications-menu-menu-item-${notification.notificationID}`}
                >
                  {notification.notificationStatusCode === 'COMPLETED' ? (
                    <SuccessIcon
                      className="notifications-menu__icon"
                      aria-hidden="true"
                    />
                  ) : notification.notificationStatusCode === 'ERROR' ? (
                    <ErrorIcon
                      className="notifications-menu__icon"
                      aria-hidden="true"
                      data-testid="notification-error-icon"
                    />
                  ) : (
                    <SpinnerIcon
                      className="notifications-menu__icon"
                      aria-hidden="true"
                    />
                  )}
                  <Box className="notifications-menu__item-content">
                    <ListItemText
                      className={`typo-text label2_size ${
                        notification.notificationStatusCode === 'COMPLETED'
                          ? 'notifications-menu__item-status-completed'
                          : notification.notificationStatusCode === 'ERROR'
                            ? 'notifications-menu__item-status-failed'
                            : 'notifications-menu__item-status-incompleted'
                      }`}
                      primary={statusText}
                    />
                    <ListItemText
                      className="typo-text label2_size notifications-menu__item-type"
                      primary={message}
                    />
                  </Box>
                </MenuItem>
              );
            })}
          </Box>
        )}
      </Menu>
      {showErrorToast && (
        <ArrToastNotification
          message={t('backgroundTasks.downloadError')}
          onClose={() => setShowErrorToast(false)}
          status={[ToastType.Error]}
          data-testid="toast-message"
        />
      )}
    </>
  );
};

export default NotificationsMenu;
